import consola from 'consola';
import { get, post } from '@/js/api/sourcer';
import STATUS from '@/store/status';
import Config from '@/js/config';
import Cookies from 'js-cookie';
import candidates from './candidates/candidates';
import settings from './settings/settings';
import { sleep } from '../../utils';
import { withUserLoaded } from '../util';

const SET_STATUS = 'SET_STATUS';
const UPDATE_CANDIDATE_STATE = 'UPDATE_CANDIDATE_STATE';
const UPDATE_CANDIDATE_STATE_IMPORT = 'UPDATE_CANDIDATE_STATE_IMPORT';
const START_LOADING = 'START_LOADING';
const FINISH_LOADING = 'FINISH_LOADING';
const SET_LEFT_SWIPE_DIALOG = 'SET_LEFT_SWIPE_DIALOG';
const SET_PRD = 'SET_PRD';
const SET_LRO = 'SET_LRO';
const SET_ACTIVE_PROJECT = 'SET_ACTIVE_PROJECT';
const REMOVE_LEFT_SWIPE_DIALOG = 'REMOVE_LEFT_SWIPE_DIALOG';
const REMOVE_PRD = 'REMOVE_PRD';
const STATE_COUNT_MAP = {
  UNDER_REVIEW: 'candidates_review_count',
  APPROVED: 'candidates_approved_count',
  CONTACTED: 'candidates_contacted_count',
  INTERVIEWING: 'candidates_interviewing_count',
  REJECTED: 'candidates_rejected_count',
  NOT_INTERESTED: 'candidates_not_interested_count',
  WARM_LEADS_RECRUITER: 'candidates_warm_leads_recruiter_count',
  WARM_LEADS_QUESTION: 'candidates_warm_leads_question_count',
  WARM_LEADS_HM: 'candidates_warm_leads_hm_count',
  WARM_LEADS_HM_APPROVED: 'candidates_warm_leads_hm_approved_count',
  WARM_LEADS_AWAITING_INFO: 'candidates_warm_leads_awaiting_info_count',
  CUSTOM_1: 'candidates_custom_1_count',
  CUSTOM_2: 'candidates_custom_2_count',
  CUSTOM_3: 'candidates_custom_3_count',
  CUSTOM_4: 'candidates_custom_4_count',
  CUSTOM_5: 'candidates_custom_5_count',
};
const STATE_COUNT_MAP_V2 = {
  UNDER_REVIEW: 'CALIBRATION',
  APPROVED: 'APPROVED',
  CONTACTED: 'CONTACTED',
  INTERVIEWING: 'INTERVIEWING',
  REJECTED: 'REJECTED',
  NOT_INTERESTED: 'NOT_INTERESTED',
  WARM_LEADS_RECRUITER: 'AUTOPILOT_RECRUITER',
  WARM_LEADS_QUESTION: 'QUESTION',
  WARM_LEADS_HM: 'HIRING_MANAGER',
  WARM_LEADS_HM_APPROVED: 'HIRING_MANAGER_APPROVED',
  WARM_LEADS_AWAITING_INFO: 'AWAITING_INFO',
  CUSTOM_1: 'candidates_custom_1_count',
  CUSTOM_2: 'candidates_custom_2_count',
  CUSTOM_3: 'candidates_custom_3_count',
  CUSTOM_4: 'candidates_custom_4_count',
  CUSTOM_5: 'candidates_custom_5_count',
};
const UPDATE_CALIBRATE = 'UPDATE_CALIBRATE';
const SET_PROJECT = 'SET_PROJECT';
const State = {
  value: {},
  lro: {},
  activeProject: undefined,
  status: STATUS.NOT_LOADED,
  operation: undefined,
  leftSwipeDialogProjects: [],
  PRDProjects: [],
};

// getters
const Getters = {
  listProjects: (state) =>
    Object.values(state.value).sort((a, b) => {
      const textA = a.project_name.toUpperCase();
      const textB = b.project_name.toUpperCase();
      if (a.is_owner && !b.is_owner) {
        return -1;
      }
      if (!a.is_owner && b.is_owner) {
        return 1;
      }
      if (textA < textB) {
        return -1;
      }
      if (textA > textB) {
        return 1;
      }
      return 0;
    }),
  getProjectById: (state) => (projectId) => state.value[projectId],
  getLeftSwipeDialogProjects: (state) => (projectId) =>
    state.leftSwipeDialogProjects,
  getPRD: (state) => (projectId) => state.PRDProjects,
};

const Operation = async (isV2) => {
  try {
    const apiURL = isV2 ? Config.env.API_URL_V2 : Config.env.API_URL;
    const response = await get(`${apiURL}projects`);
    const projects = response.data;
    const map = {};
    if (projects.length) {
      projects.forEach((project) => {
        map[project.project_id] = project;
      });
    }
    return map;
  } catch (error) {
    consola.error(error);
    consola.info('Error fetching projects');
    localStorage.removeItem('session');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('email');
    localStorage.removeItem('profile_picture');
    Cookies.remove('site_version');
    Cookies.remove('hirefly_user', {
      path: '',
      domain: '.hirefly.ai',
      user: '',
    });
    window.location.reload();
  }
  return {};
};

const ensureUserLoaded = async (rootState, dispatch) => {
  if (rootState.user.status === STATUS.NOT_LOADED) {
    await dispatch('user/LOAD', null, { root: true });
  } else if (rootState.user.status === STATUS.LOADING) {
    await rootState.user.operation;
  }
  return true;
};

// actions
const Actions = (operation) => ({
  LOAD: withUserLoaded(async ({ state, rootGetters, commit }, payload) => {
    if (!(payload && payload.force)) {
      if (state.status === STATUS.LOADED) {
        return state.value;
      }

      // If there is a pending operation, wait it finish, and return value.
      if (state.status === STATUS.LOADING) {
        const response = await state.operation;
        return response;
      }
    }

    const isV2 = rootGetters['user/getIsUserV2'];

    commit(START_LOADING, { operation, isV2 });
    commit(FINISH_LOADING, {
      value: await state.operation,
    });
    return state.value;
  }),

  async waitForCalibration({ state, dispatch, rootGetters }, { projectId }) {
    if (state.status === STATUS.LOADING) {
      await state.operation;
    }
    if (!state.value[projectId]) return;
    const project = state.value[projectId];
    // Awaiting calibration state
    if (
      !(
        project.status === '4' ||
        (project.status === '5' && rootGetters['user/getIsSuperAdmin'])
      )
    )
      return;
    if (!project.active_calibration_operation) return;
    while (state.activeProject === projectId) {
      // await LRO
      // eslint-disable-next-line no-await-in-loop
      const lroResponse = await get(
        `${Config.env.API_URL_V2}lro?lro_id=${project.active_calibration_operation}`
      );

      if (
        lroResponse &&
        lroResponse.data &&
        lroResponse.data.status === 'COMPLETED' &&
        state.activeProject === projectId
      ) {
        break;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(2000);
    }
    await dispatch('LOAD', { force: true });
  },

  // TODO(jeremiah): deprecate after v2 rollout
  async postAutopilot(context, payload) {
    return post(`${Config.env.API_URL}projects/autopilot`, payload);
  },

  async idealCandidates(context, payload) {
    return post(`${Config.env.API_URL_V2}projects/flashcards/initial`, payload);
  },

  copy: withUserLoaded(
    async ({ rootState, rootGetters, dispatch }, payload) => {
      await ensureUserLoaded(rootState, dispatch);
      const url = rootGetters['user/getIsUserV2']
        ? Config.env.API_URL_V2
        : Config.env.API_URL;
      return post(`${url}projects/copy`, payload);
    }
  ),

  // TODO(jeremiah): deprecate after v2 rollout
  async reformQuery(context, { projectId }) {
    if (!projectId) {
      return;
    }

    await post(`${Config.env.API_URL}projects/reform?project_id=${projectId}`);
  },
  async importCadidateCount(context, { projectId, tab }) {
    context.commit(UPDATE_CANDIDATE_STATE_IMPORT, { projectId, tab });
  },
});

// mutations
const Mutations = {
  [SET_STATUS](state, { status }) {
    consola.info(`[${state.constructor.name}]:SetStatus ${status}`);
    state.status = status;
  },
  [SET_LEFT_SWIPE_DIALOG](state, { projectId }) {
    consola.info(
      `[${state.constructor.name}]:SET_LEFT_SWIPE_DIALOG ${projectId}`
    );
    state.leftSwipeDialogProjects.push(projectId);
  },
  [REMOVE_LEFT_SWIPE_DIALOG](state, { projectId }) {
    consola.info(
      `[${state.constructor.name}]:REMOVE_LEFT_SWIPE_DIALOG ${projectId}`
    );

    const index_to_remove = state.leftSwipeDialogProjects.indexOf(projectId);
    if (index_to_remove > -1) {
      state.leftSwipeDialogProjects.splice(index_to_remove, 1);
    }
  },
  [SET_PRD](state, { projectId, projectData }) {
    state.PRDProjects.push(projectId);
    state.PRDProjects.push(projectData);
  },
  [REMOVE_PRD](state, { projectId, projectData }) {
    state.PRDProjects.splice(0, 2);
  },
  [SET_LRO](state, { projectId, lroID }) {
    state.lro[projectId] = lroID;
  },
  [SET_ACTIVE_PROJECT](state, { projectId }) {
    state.activeProject = projectId;
  },
  [START_LOADING](state, { operation, isV2 }) {
    consola.info(`[${state.constructor.name}]:START_LOADING`);
    consola.info(operation);
    state.operation = operation(isV2);
    state.status = STATUS.LOADING;
  },
  [FINISH_LOADING](state, { value }) {
    consola.info(`[${state.constructor.name}]:FINISH_LOADING`);
    consola.info(value);
    state.value = value;
    state.status = STATUS.LOADED;
  },
  [UPDATE_CANDIDATE_STATE](state, { projectId, tab, newTab, isV2 }) {
    const project = Getters.getProjectById(state)(projectId);
    if (!project) {
      return;
    }
    // TODO(jeremiah): clean this up after v2 rollout
    if (project.candidate_batch_count) {
      const newTabCountName = newTab;
      const oldTabCountName = tab;
      if (project.candidate_batch_count[newTabCountName]) {
        project.candidate_batch_count[newTabCountName] += 1;
      } else {
        project.candidate_batch_count[newTabCountName] = 1;
      }

      if (project.candidate_batch_count[oldTabCountName]) {
        project.candidate_batch_count[oldTabCountName] -= 1;
      } else {
        project.candidate_batch_count[oldTabCountName] = 0;
      }
      state.value = { ...state.value }; // reactive for object after change deep property
      return;
    }

    if (isV2) {
      if (!project.candidate_batch_count) return;
      const newVals = {};
      newVals[newTab] = project.candidate_batch_count[newTab]
        ? project.candidate_batch_count[newTab] + 1
        : 1;
      newVals[tab] = project.candidate_batch_count[tab]
        ? project.candidate_batch_count[tab] - 1
        : 0;
      project.candidate_batch_count = Object.assign(
        project.candidate_batch_count,
        newVals
      );
      state.value = { ...state.value };
      return;
    }
    if (project[STATE_COUNT_MAP[newTab.toUpperCase()]]) {
      project[STATE_COUNT_MAP[newTab.toUpperCase()]] += 1;
    } else {
      project[STATE_COUNT_MAP[newTab.toUpperCase()]] = 1;
    }

    if (project[STATE_COUNT_MAP[tab]]) {
      project[STATE_COUNT_MAP[tab]] -= 1;
    } else {
      project[STATE_COUNT_MAP[tab]] = 0;
    }
  },
  [UPDATE_CALIBRATE](state, { projectId, value }) {
    state.value[projectId].is_in_calibration = value;
  },
  [SET_PROJECT](state, { projectId, project }) {
    state.value[projectId] = { ...state.value[projectId], ...project };
  },
  [UPDATE_CANDIDATE_STATE_IMPORT](state, { projectId, tab }) {
    const project = Getters.getProjectById(state)(projectId);
    console.log('update approved');
    if (!project) {
      return;
    }
    if (project[STATE_COUNT_MAP[tab]]) {
      project[STATE_COUNT_MAP[tab]] += 1;
    } else {
      project[STATE_COUNT_MAP[tab]] = 1;
    }
  },
};

export default {
  namespaced: true,
  modules: {
    candidates,
    settings,
  },
  state: State,
  getters: Getters,
  actions: Actions(Operation),
  mutations: Mutations,
};
